package com.merkost.handpass.admin.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import com.merkost.handpass.admin.components.AdminBadge
import com.merkost.handpass.admin.components.AdminBadgeVariant
import com.merkost.handpass.admin.components.AdminButton
import com.merkost.handpass.admin.components.AdminButtonSize
import com.merkost.handpass.admin.components.AdminButtonVariant
import com.merkost.handpass.admin.components.AdminEmptyState
import com.merkost.handpass.admin.components.AdminPageHeader
import com.merkost.handpass.admin.components.AdminPageScaffold
import com.merkost.handpass.admin.components.AdminPageScope
import com.merkost.handpass.admin.components.AdminPanel
import com.merkost.handpass.admin.components.AdminSkeletonTable
import com.merkost.handpass.admin.container.ImagesContainer
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.ImagesAction
import com.merkost.handpass.admin.contract.ImagesIntent
import com.merkost.handpass.admin.contract.ImagesState
import com.merkost.handpass.admin.di.koinContainer
import com.merkost.handpass.api.dto.admin.AdminImageListItem
import com.merkost.handpass.api.enums.ContentFlag
import com.merkost.handpass.api.enums.ImageStatus
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.HandpassRadius
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.components.WebIcon
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import pro.respawn.flowmvi.compose.dsl.subscribe

private val imageStatusFilters = listOf(
    "All" to null,
    "Pending" to ImageStatus.PENDING,
    "Needs review" to ImageStatus.NEEDS_REVIEW,
    "Rejected" to ImageStatus.REJECTED,
    "Approved" to ImageStatus.APPROVED,
)

@Page("/images")
@Composable
fun ImagesPage() {
    AdminPageScaffold(title = "Image Review", currentPath = "/images", showTitle = false) {
        val container = koinContainer<ImagesContainer>()
        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is ImagesAction.Navigate -> navigate(action.path)
                }
            }
            ImagesContent(state, onIntent = ::intent)
        }
    }
}

@Composable
private fun AdminPageScope.ImagesContent(state: ImagesState, onIntent: (ImagesIntent) -> Unit) {
    AdminPageHeader(
        title = "Image Review",
        subtitle = "Review and approve/reject uploaded images",
        actions = {
            AdminButton(
                label = "Refresh",
                onClick = { onIntent(ImagesIntent.Refresh) },
                variant = AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        },
    )

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(HandpassSpacing.SM)
            property("flex-wrap", "wrap")
            property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) {
        imageStatusFilters.forEach { (label, status) ->
            AdminButton(
                label = label,
                onClick = { onIntent(ImagesIntent.SetFilter(status)) },
                variant = if (state.statusFilter == status) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
    }

    when (val data = state.images) {
        AsyncSection.Loading -> AdminPanel(title = "Images") { AdminSkeletonTable(rows = 6) }
        is AsyncSection.Failed -> AdminEmptyState(
            title = "Failed to load",
            description = data.message,
            actionLabel = "Retry",
            onAction = { onIntent(ImagesIntent.Refresh) },
            icon = WebIcon.Eye,
        )
        is AsyncSection.Loaded -> {
            if (data.value.isEmpty()) {
                AdminEmptyState(title = "No images found", icon = WebIcon.Eye)
            } else {
                AdminPanel(title = "Images", count = data.value.size) {
                    ImageGrid(
                        images = data.value,
                        pendingIds = state.pendingIds,
                        onIntent = onIntent,
                        onNavigateListing = { id -> navigate("/listings/$id") },
                    )
                }
            }
        }
    }
}

@Composable
private fun ImageGrid(
    images: List<AdminImageListItem>,
    pendingIds: Set<String>,
    onIntent: (ImagesIntent) -> Unit,
    onNavigateListing: (String) -> Unit,
) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(220px, 1fr))")
            gap(HandpassSpacing.Default)
        }
    }) {
        images.forEach { item ->
            ImageCard(
                item = item,
                pending = item.id in pendingIds,
                onIntent = onIntent,
                onNavigateListing = onNavigateListing,
            )
        }
    }
}

@Composable
private fun ImageCard(
    item: AdminImageListItem,
    pending: Boolean,
    onIntent: (ImagesIntent) -> Unit,
    onNavigateListing: (String) -> Unit,
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(HandpassSpacing.MD)
            padding(HandpassSpacing.MD)
            borderRadius(HandpassRadius.MD)
            property("background-color", Surface.value().toString())
            property("border", "1px solid ${Border.value()}")
            property("box-shadow", "var(--hp-shadow-card)")
            property("min-width", "0")
        }
    }) {
        Img(src = item.storageUrl, attrs = {
            style {
                width(100.percent)
                height(160.px)
                property("object-fit", "cover")
                property("border-radius", "8px")
                property("background-color", SurfaceMuted.value().toString())
                property("outline", "1px solid rgba(0, 0, 0, 0.08)")
            }
        })
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                gap(HandpassSpacing.SM)
            }
        }) {
            AdminBadge(label = statusLabel(item.status), variant = statusVariant(item.status))
            Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()) } }) {
                Text(item.createdAt.take(10))
            }
        }
        ImageMetadata(item = item, onNavigateListing = onNavigateListing)
        ImageSignals(item = item)
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(HandpassSpacing.SM)
                property("margin-top", "auto")
            }
        }) {
            AdminButton(
                label = "Approve",
                onClick = { onIntent(ImagesIntent.Approve(item.id)) },
                variant = AdminButtonVariant.Primary,
                size = AdminButtonSize.SM,
                enabled = !pending,
                loading = pending,
                fillWidth = true,
            )
            AdminButton(
                label = "Reject",
                onClick = { onIntent(ImagesIntent.Reject(item.id)) },
                variant = AdminButtonVariant.Destructive,
                size = AdminButtonSize.SM,
                enabled = !pending,
                loading = pending,
                fillWidth = true,
            )
        }
    }
}

@Composable
private fun ImageMetadata(item: AdminImageListItem, onNavigateListing: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.XS) } }) {
        Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
            Text(item.uploaderName ?: item.uploadedBy)
        }
        val title = item.listingTitle ?: "No listing attached"
        val listingId = item.listingId
        if (listingId != null && item.listingTitle != null) {
            Button(attrs = {
                onClick { onNavigateListing(listingId) }
                style {
                    padding(0.px)
                    margin(0.px)
                    property("background", "transparent")
                    property("border", "0")
                    property("text-align", "left")
                    property("cursor", "pointer")
                    fontSize(HandpassTypography.Body)
                    fontWeight(600)
                    color(Accent.value())
                }
            }) {
                Text(title)
            }
        } else {
            Span({ style { fontSize(HandpassTypography.Body); fontWeight(600); color(OnSurface.value()) } }) {
                Text(title)
            }
        }
    }
}

@Composable
private fun ImageSignals(item: AdminImageListItem) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.XS) } }) {
        if (item.flags.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(HandpassSpacing.XS)
                    property("flex-wrap", "wrap")
                }
            }) {
                item.flags.forEach { flag ->
                    AdminBadge(
                        label = flagLabel(flag),
                        variant = AdminBadgeVariant.Warning,
                        compact = true,
                        showDot = false,
                    )
                }
            }
        }
        item.confidence?.let { confidence ->
            Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
                Text("${(confidence * 100).toInt()}% confidence")
            }
        }
    }
}

private fun statusLabel(status: ImageStatus): String = when (status) {
    ImageStatus.APPROVED -> "Approved"
    ImageStatus.REJECTED -> "Rejected"
    ImageStatus.NEEDS_REVIEW -> "Needs review"
    ImageStatus.PENDING -> "Pending"
}

private fun statusVariant(status: ImageStatus): AdminBadgeVariant = when (status) {
    ImageStatus.APPROVED -> AdminBadgeVariant.Success
    ImageStatus.REJECTED -> AdminBadgeVariant.Error
    ImageStatus.NEEDS_REVIEW -> AdminBadgeVariant.Warning
    ImageStatus.PENDING -> AdminBadgeVariant.Neutral
}

private fun flagLabel(flag: ContentFlag): String =
    flag.name.lowercase().replaceFirstChar { it.uppercase() }
